import SurveyHeader from "@/containers/survey/SurveyHeader";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import ImgPreview from "@/components/ImgPreview";
import { AddressProofType } from "@/interfaces/surveyTypes";
import Head from "next/head";
import ContentContainer from "@/containers/common/ContentContainer";

interface Props {
	type: number;
	onCloseClick: () => void;
}

export default function Samples({ type, onCloseClick }: Props) {
	const { t } = useTranslation("survey");
	const router = useRouter();
	const scrollRef = useRef<any[]>([]);

	const getLocale = () => (router.locale?.toLocaleLowerCase() === "zh-hans" ? "-zh-Hans" : "");
	const series = [
		{
			title: t("utility-bills-explained"),
			images: [`/h5/survey/utility-bills${getLocale()}.png`],
			desc: [
				t("utility-bills-include-electricity-water-gas-or-landline-bill"),
				t("the-date-of-issuance-must-be-within-the"),
				t("any-address-certificate-must-include-full-name"),
				t("please-ensure-that-materials"),
			],
			extend: "",
			id: AddressProofType.UTILITY_BILL,
		},
		{
			title: t("government-documents-explained"),
			images: [
				`/h5/survey/government-documents-1${getLocale()}.png`,
				`/h5/survey/government-documents-2${getLocale()}.png`,
			],
			desc: [
				t("government-documents-cover-government-letters"),
				t("the-date-of-issuance-must-be-within-the"),
				t("any-address-certificate-must-include-full-name"),
				t("please-ensure-that-materials"),
			],
			extend: t("currently-only-supported-with-address-information"),
			id: AddressProofType.GOVERNMENT_ISSUED_LETTERS,
		},
		{
			title: t("lease-agreement-explained"),
			images: [`/h5/survey/lease-agreement${getLocale()}.png`],
			desc: [
				t("the-date-of-issuance-must-be-within-the"),
				t("any-address-certificate-must-include-full-name"),
				t("please-ensure-that-materials"),
			],
			extend: "",
			id: AddressProofType.CURRENT_LEASE,
		},
		{
			title: t("bank-statements-explained"),
			images: [`/h5/survey/bank-statements${getLocale()}.png`],
			desc: [
				t("bills-issued-by-financial-institutions-such-as"),
				t("the-date-of-issuance-must-be-within-the"),
				t("any-address-certificate-must-include-full-name"),
				t("please-ensure-that-materials"),
			],
			extend: "",
			id: AddressProofType.BANK_STATEMENT,
		},
	];

	useEffect(() => {
		scrollRef.current[type].scrollIntoView({
			behavior: "smooth",
			block: "start",
			inline: "start",
		});
	}, []);

	return (
		<>
			<Head>
				<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
			</Head>

			<ContentContainer
				className="zh-letter-space"
				padding
				header={<SurveyHeader space hiddenBackBtn onCloseClick={onCloseClick} />}
			>
				{series.map((item, key) => {
					return (
						<div key={key}>
							<div className="flex items-center">
								<span className="w-2 h-5 bg-primary mr-2 dark:bg-white"></span>
								<p
									className="text-xl font-semibold"
									ref={(el) => {
										scrollRef.current[item.id] = el;
									}}
								>
									{item.title}
								</p>
							</div>
							<div className="mt-4">
								{item.desc.map((tex, index) => {
									return (
										<p key={index + "t"} className="mb-5 font-normal text-sm">
											&#8226; {tex}
										</p>
									);
								})}
								{item.extend && <p className="mb-5 font-normal text-sm">{item.extend}</p>}
							</div>
							{item.images.map((path) => (
								<ImgPreview key={path} path={path} />
							))}
						</div>
					);
				})}
			</ContentContainer>
		</>
	);
}
